import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

export default function EditImage({ document, onClose, onUpdate, chatbotId, permission }) {
  const [formData, setFormData] = useState({
    filename: "",
    image_content: "",
    summary: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (document) {
      setFormData({
        filename: document.filename || "",
        image_content: document.image_content || "",
        summary: document.summary || "",
      });
    }
  }, [document]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!permission?.canModify) {
      setError("You don't have permission to edit image data.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      await axiosInstance.post(
        `/update-image`,
        {
          id: document.id,
          filename: formData.filename,
          image_content: formData.image_content,
          summary: formData.summary,
        },
        { params }
      );

      setSuccess("Image data updated successfully!");
      setTimeout(() => {
        onUpdate();
      }, 1500);
    } catch (error) {
      console.error("Error updating image data:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update image data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={700} color="textPrimary">
          Edit Image Data
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ maxHeight: "calc(90vh - 180px)", overflow: "auto", pr: 1 }}
      >
        <TextField
          fullWidth
          label="Filename"
          value={formData.filename}
          onChange={handleChange("filename")}
          margin="normal"
          required
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Summary"
          value={formData.summary}
          onChange={handleChange("summary")}
          margin="normal"
          multiline
          rows={3}
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Image Content"
          value={formData.image_content}
          onChange={handleChange("image_content")}
          margin="normal"
          multiline
          rows={6}
          required
          disabled={loading}
        />

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading || !permission?.canModify}
            fullWidth
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={loading}
            fullWidth
          >
            Cancel
          </Button>
        </Stack>

        {!permission?.canModify && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            You don't have permission to edit image data.
          </Alert>
        )}
      </Box>
    </Box>
  );
}